import React, { useRef } from "react";
import MainAppBar from "../../components/atoms/appbar/MainAppbar";
import CommunityCard from "../../components/atoms/card/CommunityCard";
import AnimatedCoverTabs from "../../components/atoms/tab/AnimatedConverTab";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCommunityArticleList } from "../../@apis/hooks/useCommunity";
import FloatingButton from "../../components/atoms/button/FloatingButton";
import { useMainGolfParkDetail } from "../../@apis/hooks/useMain";
import { vw } from "../../lib/utils/responsiveUtils";
import { staticURL } from "../../lib/utils/imageUtils";
import { cn } from "../../lib/utils/cn";
import { useLoginInfo } from "../../hooks/useLoginInfo";
import { useModalStore } from "../../lib/store/useModalStore";
import { LOGIN_DIALOG_LABEL } from "../../lib/constants/common.constants";
import { useDeviceStore } from "../../lib/store/useDeviceStore";
import { saveScrollPosition } from "../../hooks/useMainScrollRestoration";

const TAB_LABELS = [
  { label: "최신순", value: "NEWEST" },
  { label: "인기순", value: "POPULARITY" },
];

export default function Community() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { deviceType } = useDeviceStore();

  const store = searchParams.get("store");
  const articleSortBy =
    (searchParams.get("sort") as "NEWEST" | "POPULARITY") ?? "NEWEST";

  const form = {
    articleSortBy,
    golfParkCodeList: [store],
  };

  const { isLoggedIn } = useLoginInfo();
  const { openModal } = useModalStore();

  const cameFromDetailRef = React.useRef(
    sessionStorage.getItem("community_came_from_detail") === "true",
  );
  const savedScrollRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("scroll_community") ?? "0")
      : 0,
  );
  const savedPagesRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("community_scroll_pages") ?? "1")
      : 1,
  );
  const scrollRestoredRef = React.useRef(false);
  const restoreCleanupRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    if (!cameFromDetailRef.current) {
      sessionStorage.removeItem("scroll_community");
      sessionStorage.removeItem("community_scroll_pages");
    }
    sessionStorage.removeItem("community_came_from_detail");
  }, []);

  const {
    data: CommunityData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    ...useCommunityArticleList(form),
    staleTime: 3 * 60 * 1000,
  });
  const communityPages = CommunityData?.pages || [];

  // 글 작성/수정 후 돌아온 경우: 캐시된 페이지로 스크롤을 복원하면서
  // 같은 페이지들을 제자리(in-place) refetch 해 최신 내용으로 갱신한다.
  React.useEffect(() => {
    if (sessionStorage.getItem("community_needs_refresh") === "true") {
      sessionStorage.removeItem("community_needs_refresh");
      refetch();
    }
  }, [refetch]);

  const { data: storeData, isSuccess: storeSuccess } = useQuery({
    ...useMainGolfParkDetail(store || ""),
    enabled: !!store,
  });
  const { golfPark } = storeData?.data || {};

  const triggerRef = useRef(null);

  const firstPageArticles = communityPages[0]?.data?.articleList;

  // 리프레시 후 복원: 저장된 페이지 수만큼 fetchNextPage 호출
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (communityPages.length >= savedPagesRef.current) return;
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [communityPages.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // 충분한 페이지가 로드되면 스크롤 복원
  const enoughPagesLoaded =
    communityPages.length >= savedPagesRef.current || !hasNextPage;
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (!enoughPagesLoaded || !firstPageArticles?.length) return;

    scrollRestoredRef.current = true;

    const saved = savedScrollRef.current;
    const mainEl = document.querySelector("main") as HTMLElement | null;
    if (!saved || !mainEl) return;

    const apply = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          mainEl.scrollTop = saved;
        }),
      );

    apply();
    // 돌아온 직후 refetch로 콘텐츠가 교체되거나 이미지가 로드되며 높이가 바뀌어도
    // 일정 시간 동안 스크롤 위치를 재적용해 풀리지 않게 한다.
    const observer = new ResizeObserver(apply);
    observer.observe(mainEl);
    const timer = setTimeout(() => observer.disconnect(), 800);

    // 재렌더로 이 이펙트가 다시 실행돼도 옵저버를 끊지 않도록 cleanup은 ref에만 보관
    restoreCleanupRef.current = () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [enoughPagesLoaded, firstPageArticles]);

  // 언마운트 시에만 복원 옵저버 정리
  React.useEffect(() => () => restoreCleanupRef.current?.(), []);

  React.useEffect(() => {
    const mainEl = document.querySelector("main");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: mainEl, threshold: 0 },
    );

    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="bg-[#f5f5f5] min-h-full relative flex flex-col overflow-x-hidden">
      <MainAppBar title="커뮤니티" search={false} />
      <div className="absolute top-[56px] left-0 w-full h-[60px] bg-gradient-to-t from-transparent to-white pointer-events-none" />
      <div className="relative px-[15px] flex-1 flex flex-col">
        <AnimatedCoverTabs
          tabs={TAB_LABELS}
          value={articleSortBy}
          onChange={(value) =>
            setSearchParams(
              (prev) => {
                const n = new URLSearchParams(prev);
                n.set("sort", value);
                return n;
              },
              { replace: true },
            )
          }
          className="z-10"
        />
        <section className="mt-[20px] space-y-[15px] flex-1 flex flex-col">
          {!!store && storeSuccess && (
            <div className="flex-row bg-white px-[20px] py-[14px] rounded-[10px] gap-x-[20px] mt-[10px] items-start">
              <div
                className={cn(
                  `rounded-[15px] shadow-[0px 15px 30px 0 rgba(0,0,0,0.15)] bg-[#eee] flex-row mt-[6px]`,
                )}
              >
                {golfPark?.imageUrl ? (
                  <img
                    src={staticURL(golfPark.imageUrl)}
                    alt="_blank"
                    className="rounded-[10px] shrink-0"
                    style={{ width: vw(83), height: vw(83) }}
                  />
                ) : (
                  <img
                    src="/images/img-login-logo.webp"
                    alt="_blank"
                    className="rounded-[10px] shrink-0"
                    style={{ width: vw(83), height: vw(83) }}
                  />
                )}
              </div>
              <div>
                <p className="font-bold text-2xl">{golfPark?.name}</p>
                <div className="flex-row gap-[5px] items-start mt-[14px] font-[500] text-[#888] text-sm">
                  <img
                    className="size-3.5 mt-1"
                    src="/icons/ic-place-gray.webp"
                  />
                  <p>{golfPark?.address}</p>
                </div>
              </div>
            </div>
          )}
          {(communityPages[0]?.data?.articleList?.length ?? 0) === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center pb-[50px] text-[#ccc]">
              <img
                src={"/icons/ic-notice.webp"}
                alt="게시글 없음"
                className="mb-[14px]"
                style={{ width: vw(48), height: vw(48) }}
              />
              <p>게시글이 없습니다.</p>
            </div>
          ) : (
            (communityPages || []).map((pageData, pageIndex) => (
              <React.Fragment key={pageIndex}>
                {pageData.data.articleList.map((item, index, arr) => {
                  const isLast =
                    pageIndex === communityPages.length - 1 &&
                    index === arr.length - 1;
                  return (
                    <CommunityCard
                      ref={isLast ? triggerRef : undefined}
                      variant={"white"}
                      item={item}
                      key={index}
                      onClickCapture={() => {
                        saveScrollPosition("community");
                        sessionStorage.setItem(
                          "community_came_from_detail",
                          "true",
                        );
                        sessionStorage.setItem(
                          "community_scroll_pages",
                          String(communityPages.length),
                        );
                        // 목록의 필터(store)/정렬(sort) 상태를 저장해, 상세에서
                        // 삭제 후 같은 목록으로 돌아갈 때 복원한다.
                        sessionStorage.setItem("community_store", store ?? "");
                        sessionStorage.setItem("community_sort", articleSortBy);
                      }}
                    />
                  );
                })}
              </React.Fragment>
            ))
          )}
        </section>
      </div>
      <div className="fixed bottom-[77px] right-[15px] z-50">
        <FloatingButton
          onClick={() =>
            !isLoggedIn
              ? openModal("CONFIRM_DIALOG", {
                  title: LOGIN_DIALOG_LABEL.title,
                  content: LOGIN_DIALOG_LABEL.content,
                  onClick: () => navigate("/login"),
                })
              : deviceType !== "android"
                ? navigate(`/community/add${store ? `?store=${store}` : ""}`)
                : window.open(
                    `/community/add${store ? `?store=${store}` : ""}`,
                    "_blank",
                  )
          }
        />
      </div>
    </div>
  );
}
